import { useEffect, useState } from 'react'

import { useDebouncedCallback } from '@/hooks/useDebouncedCallback'

/**
 * Debounces a reactive value: returns `value` immediately on mount, then only
 * updates the returned value once `value` has stopped changing for `delay` ms.
 * Rapid intermediate changes are coalesced, so the returned value jumps straight
 * to the latest one.
 *
 * Use it to keep a fast-updating value (e.g. a quantity counter that must feel
 * instant) out of an expensive derived operation (e.g. a per-quantity price
 * query) — drive the UI with the raw value and the query with the debounced one.
 *
 * @param value The value to debounce
 * @param delay Delay in milliseconds (defaults to the shared 300ms)
 * @example
 * const debouncedQuantity = useDebouncedValue(quantity, 500)
 * const { priceData } = useProductPrice(code, debouncedQuantity)
 */
export function useDebouncedValue<T>(value: T, delay = 300): T {
  const [debouncedValue, setDebouncedValue] = useState(value)
  const { debouncedCallback } = useDebouncedCallback((next: T) => setDebouncedValue(next), delay)

  useEffect(() => {
    debouncedCallback(value)
  }, [value, debouncedCallback])

  return debouncedValue
}
